/**
 * L'involucro comune di tutte le icone disegnate a mano.
 *
 * Stava dentro `MenuUtente`, dove è nato. È uscito quando l'interruttore delle
 * notifiche è diventato un componente a sé — usato sia dal menu sia dalla
 * pagina del profilo — e si è portato dietro la sua campana: senza un posto
 * comune l'unica alternativa era ridisegnare lo stesso involucro nel secondo
 * file, e da lì in poi due copie da tenere allineate a mano.
 *
 * Stesso impianto per tutte le icone dell'applicazione: 24 unità di lato, solo
 * tratto, `currentColor` — così una voce disabilitata o rossa si tira dietro
 * anche il suo disegno senza doverlo ridichiarare.
 */
import type { ReactNode } from 'react';

export function Icona({ children, lato = 16 }: { children: ReactNode; lato?: number }) {
  return (
    <svg
      width={lato}
      height={lato}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/**
 * La campana degli avvisi.
 *
 * Sta qui e non in `InterruttoreNotifiche`, dov'era nata, perché dal 4b la
 * usano in due: l'interruttore e l'elenco delle scadenze. Due copie dello
 * stesso tracciato sarebbero due disegni da tenere allineati a mano — è già
 * successo col glifo del marchio, ed è fra i punti fragili del piano.
 */
export function IconaCampana({ lato }: { lato?: number }) {
  return (
    <Icona {...(lato === undefined ? {} : { lato })}>
      <path d="M6.6 10a5.4 5.4 0 0 1 10.8 0c0 4 1.6 5.6 1.6 5.6H5s1.6-1.6 1.6-5.6Z" />
      <path d="M10 18.6a2.2 2.2 0 0 0 4 0" />
    </Icona>
  );
}

/**
 * La stessa campana, sbarrata: «di questo non avvisa nessuno».
 *
 * Il taglio è una diagonale sola, dall'angolo all'angolo. Le versioni che
 * spezzano il disegno della campana sono più eleganti a 24 pixel e
 * illeggibili a 14, che è la misura a cui questa vive: in una colonna di
 * tabella, letta di sfuggita scorrendo l'elenco.
 */
export function IconaCampanaBarrata({ lato }: { lato?: number }) {
  return (
    <Icona {...(lato === undefined ? {} : { lato })}>
      <path d="M6.6 10a5.4 5.4 0 0 1 10.8 0c0 4 1.6 5.6 1.6 5.6H5s1.6-1.6 1.6-5.6Z" />
      <path d="M10 18.6a2.2 2.2 0 0 0 4 0" />
      <path d="M4.4 4.4 19.6 19.6" />
    </Icona>
  );
}
